import { useCallback, useEffect, useMemo, useState } from "react";
import { addDaysToDateKey, DEFAULT_TIMEZONE, formatLocalDateKey } from "@if-tracking/shared";
import type { Fast, Settings, User } from "@if-tracking/shared";

import { api, ApiError, type StatsResponse } from "./api";
import { formatDate } from "./format";
import { Login } from "./components/Login";
import { FastList } from "./components/FastList";
import { BarChart } from "./components/BarChart";
import { StatsSummary } from "./components/StatsSummary";
import { CalendarHeatmap } from "./components/CalendarHeatmap";
import { FastFormModal } from "./components/FastFormModal";
import { ImportExport } from "./components/ImportExport";
import { SettingsPanel } from "./components/SettingsPanel";
import { Toast, type ToastState } from "./components/Toast";

type Tab = "history" | "data" | "settings";
type Range = "last7d" | "last30d" | "calendar" | "all";
type AuthState = "checking" | "out" | "in";

const HISTORY_LIMIT = 1000;

const RANGES: { key: Range; label: string }[] = [
  { key: "last7d", label: "Last 7 Days" },
  { key: "last30d", label: "Last 30 Days" },
  { key: "calendar", label: "Calendar" },
  { key: "all", label: "All" }
];

export function App() {
  const [auth, setAuth] = useState<AuthState>("checking");
  const [user, setUser] = useState<User | null>(null);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [fasts, setFasts] = useState<Fast[]>([]);
  const [loadingFasts, setLoadingFasts] = useState(false);
  const [tab, setTab] = useState<Tab>("history");
  const [range, setRange] = useState<Range>("last7d");
  const [stats, setStats] = useState<StatsResponse | null>(null);
  const [monthPrefix, setMonthPrefix] = useState<string>(() =>
    formatLocalDateKey(Date.now(), DEFAULT_TIMEZONE).slice(0, 7)
  );
  const [toast, setToast] = useState<ToastState | null>(null);
  const [editing, setEditing] = useState<{ open: boolean; fast: Fast | null }>({ open: false, fast: null });

  const notify = useCallback((message: string, kind: ToastState["kind"] = "info") => {
    setToast({ message, kind });
  }, []);

  const timezone = settings?.timezone ?? DEFAULT_TIMEZONE;

  const loadFasts = useCallback(async () => {
    setLoadingFasts(true);
    try {
      const rows = await api.listFasts({ limit: HISTORY_LIMIT });
      setFasts(rows);
    } catch (error) {
      if (error instanceof ApiError && error.isAuth) {
        setAuth("out");
        return;
      }
      notify(error instanceof Error ? error.message : "Failed to load fasts", "error");
    } finally {
      setLoadingFasts(false);
    }
  }, [notify]);

  const bootstrap = useCallback(async () => {
    try {
      // `me` doubles as the authenticated probe that decides the login screen.
      setUser(await api.me());
      const loaded = await api.getSettings();
      setSettings(loaded);
      setMonthPrefix(formatLocalDateKey(Date.now(), loaded.timezone).slice(0, 7));
      setAuth("in");
      await loadFasts();
    } catch (error) {
      if (error instanceof ApiError && error.isAuth) {
        setAuth("out");
        return;
      }
      notify(error instanceof Error ? error.message : "Failed to connect", "error");
      setAuth("out");
    }
  }, [loadFasts, notify]);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  // Load stats whenever a stats-backed range is active (and after data changes).
  const loadStats = useCallback(async () => {
    if (range !== "last7d" && range !== "last30d") return;
    try {
      const response = await api.getStats(range === "last7d" ? "last_7d" : "last_30d");
      setStats(response);
    } catch (error) {
      if (error instanceof ApiError && error.isAuth) {
        setAuth("out");
        return;
      }
      notify(error instanceof Error ? error.message : "Failed to load stats", "error");
    }
  }, [range, notify]);

  useEffect(() => {
    if (auth === "in") void loadStats();
  }, [auth, loadStats, fasts]);

  const handleLoggedIn = useCallback(
    async (signedIn: User) => {
      setUser(signedIn);
      setAuth("checking");
      await bootstrap();
    },
    [bootstrap]
  );

  const handleLogout = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      // ignore
    }
    setUser(null);
    setSettings(null);
    setFasts([]);
    setStats(null);
    setAuth("out");
  }, []);

  const openCreate = useCallback(() => setEditing({ open: true, fast: null }), []);
  const openEdit = useCallback((fast: Fast) => setEditing({ open: true, fast }), []);
  const closeForm = useCallback(() => setEditing({ open: false, fast: null }), []);

  const handleSaved = useCallback(
    async (message: string) => {
      closeForm();
      notify(message, "success");
      await loadFasts();
    },
    [closeForm, loadFasts, notify]
  );

  const handleDelete = useCallback(
    async (fast: Fast) => {
      const when = formatDate(fast.start_ts, timezone);
      if (!window.confirm(`Delete the fast starting ${when}? This cannot be undone.`)) return;
      try {
        await api.deleteFast(fast.id);
        notify("Fast deleted", "success");
        await loadFasts();
      } catch (error) {
        notify(error instanceof Error ? error.message : "Delete failed", "error");
      }
    },
    [loadFasts, notify, timezone]
  );

  const filteredFasts = useMemo(() => {
    if (range === "all") return fasts;
    const keyOf = (f: Fast) => formatLocalDateKey(f.start_ts, timezone);
    if (range === "calendar") {
      return fasts.filter((f) => keyOf(f).startsWith(monthPrefix));
    }
    const todayKey = formatLocalDateKey(Date.now(), timezone);
    const fromKey = addDaysToDateKey(todayKey, range === "last7d" ? -6 : -29);
    return fasts.filter((f) => keyOf(f) >= fromKey);
  }, [fasts, range, monthPrefix, timezone]);

  const earliestYear = useMemo(() => {
    if (fasts.length === 0) return null;
    const min = fasts.reduce((acc, f) => Math.min(acc, f.start_ts), Infinity);
    return new Intl.DateTimeFormat("en-US", { timeZone: timezone, year: "numeric" }).format(new Date(min));
  }, [fasts, timezone]);

  if (auth === "checking") {
    return (
      <div className="centered">
        <p className="muted">Loading…</p>
      </div>
    );
  }

  if (auth === "out") {
    return (
      <>
        <Login onLoggedIn={handleLoggedIn} onError={(m) => notify(m, "error")} />
        <Toast toast={toast} onDismiss={() => setToast(null)} />
      </>
    );
  }

  return (
    <div className="app">
      <header className="app-header">
        <div className="brand">
          <span className="brand-mark">◴</span>
          <span>Fasting</span>
          {user && <span className="account-chip" title={`Signed in as ${user.username}`}>{user.display_name}</span>}
        </div>
        <nav className="tabs">
          <button
            className={tab === "history" ? "tab active" : "tab"}
            aria-current={tab === "history" ? "page" : undefined}
            onClick={() => setTab("history")}
          >
            History
          </button>
          <button
            className={tab === "data" ? "tab active" : "tab"}
            aria-current={tab === "data" ? "page" : undefined}
            onClick={() => setTab("data")}
          >
            Data
          </button>
          <button
            className={tab === "settings" ? "tab active" : "tab"}
            aria-current={tab === "settings" ? "page" : undefined}
            onClick={() => setTab("settings")}
          >
            Settings
          </button>
        </nav>
      </header>

      <main className="app-main">
        {tab === "history" && (
          <section className="panel">
            <div className="panel-head">
              <h1>History</h1>
              <button className="icon-button" onClick={openCreate} aria-label="Add fast" title="Add fast">
                +
              </button>
            </div>

            <div className="segmented" role="group" aria-label="Date range">
              {RANGES.map((r) => (
                <button
                  key={r.key}
                  className={range === r.key ? "active" : ""}
                  aria-pressed={range === r.key}
                  onClick={() => setRange(r.key)}
                >
                  {r.label}
                </button>
              ))}
            </div>

            {(range === "last7d" || range === "last30d") && (
              <>
                <BarChart days={stats?.daily ?? []} timezone={timezone} />
                {stats && <StatsSummary stats={stats} />}
              </>
            )}

            {range === "calendar" && settings && (
              <CalendarHeatmap
                fasts={fasts}
                timezone={timezone}
                weekStart={settings.week_start}
                onMonthChange={setMonthPrefix}
              />
            )}

            <div className="stack" style={{ marginBottom: 0 }}>
              <span className="section-label">
                {range === "all"
                  ? `All fasts${earliestYear ? ` · back to ${earliestYear}` : ""}${fasts.length >= HISTORY_LIMIT ? ` · most recent ${HISTORY_LIMIT}` : ""}`
                  : RANGES.find((r) => r.key === range)?.label}
              </span>
              {loadingFasts && fasts.length === 0 ? (
                <div className="empty">Loading…</div>
              ) : (
                <FastList
                  fasts={filteredFasts}
                  timezone={timezone}
                  grouped={range === "all"}
                  emptyHint="No fasts in this range."
                  onEdit={openEdit}
                  onDelete={handleDelete}
                />
              )}
            </div>
          </section>
        )}

        {tab === "data" && <ImportExport onImported={loadFasts} notify={notify} />}

        {tab === "settings" && settings && user && (
          <SettingsPanel
            settings={settings}
            user={user}
            onSaved={(updated) => {
              setSettings(updated);
              notify("Settings saved", "success");
            }}
            onSignOut={handleLogout}
            notify={notify}
          />
        )}
      </main>

      {editing.open && settings && (
        <FastFormModal
          fast={editing.fast}
          timezone={timezone}
          defaultTargetSeconds={settings.default_target_seconds}
          onClose={closeForm}
          onSaved={handleSaved}
          onError={(m) => notify(m, "error")}
        />
      )}

      <Toast toast={toast} onDismiss={() => setToast(null)} />
    </div>
  );
}
